import { useEffect, useState, FormEvent } from 'react';
import { AppShell } from '../components/AppShell';
import { Modal } from '../components/Modal';
import { DictateButton } from '../components/DictateButton';
import { BulkImportModal } from '../components/BulkImportModal';
import { useConfirm } from '../components/ConfirmProvider';
import { useUserOrg } from '../lib/org';
import { LoadingIndicator } from '../components/LoadingIndicator';
import {
  listCoshh,
  createCoshh,
  updateCoshh,
  archiveCoshh,
  type CoshhItem,
} from '../lib/db';
import { cacheListByOrg } from '../lib/cache';

interface Draft {
  product: string;
  used_for: string;
  hazards: string;
  controls: string;
}

const EMPTY: Draft = { product: '', used_for: '', hazards: '', controls: '' };

export function RegisterCOSHH() {
  const { org } = useUserOrg();
  const { confirm } = useConfirm();
  const [items, setItems] = useState<CoshhItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<CoshhItem | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  async function refresh() {
    if (!org) return;
    setLoading(true);
    try {
      setItems(await listCoshh(org.org_id));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  // Initial load — stale-while-revalidate. Post-mutation refreshes
  // still call refresh() directly for network-only behaviour.
  useEffect(() => {
    if (!org) return;
    let cancelled = false;
    let haveCache = false;
    cacheListByOrg<CoshhItem>('coshh', org.org_id).then((cached) => {
      if (cancelled) return;
      if (cached.length > 0) {
        haveCache = true;
        setItems(cached);
        setLoading(false);
      }
    }).catch(() => {});
    listCoshh(org.org_id).then((list) => {
      if (cancelled) return;
      setItems(list);
      setError(null);
    }).catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled && !haveCache) setLoading(false); });
    return () => { cancelled = true; };
    /* eslint-disable-next-line */
  }, [org]);

  function startAdd() {
    setEditing(null);
    setDraft(EMPTY);
    setShowModal(true);
  }
  function startEdit(item: CoshhItem) {
    setEditing(item);
    setDraft({
      product: item.product,
      used_for: item.used_for ?? '',
      hazards: item.hazards ?? '',
      controls: item.controls ?? '',
    });
    setShowModal(true);
  }

  async function onSave(e: FormEvent) {
    e.preventDefault();
    if (!org) return;
    if (!draft.product.trim()) { setError('Please enter what the product is.'); return; }
    setSubmitting(true);
    try {
      if (editing) {
        await updateCoshh(editing.id, {
          product: draft.product.trim(),
          used_for: draft.used_for.trim() || null,
          hazards: draft.hazards.trim() || null,
          controls: draft.controls.trim() || null,
        });
      } else {
        await createCoshh({
          orgId: org.org_id,
          product: draft.product.trim(),
          used_for: draft.used_for.trim() || null,
          hazards: draft.hazards.trim() || null,
          controls: draft.controls.trim() || null,
        });
      }
      setShowModal(false);
      setEditing(null);
      setDraft(EMPTY);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  async function onArchive(item: CoshhItem) {
    if (!(await confirm({
      title: `Archive "${item.product}"?`,
      body: 'It\'ll be hidden from the COSHH register. Existing records stay on file.',
      confirmLabel: 'Archive',
      danger: true,
    }))) return;
    try {
      await archiveCoshh(item.id);
      setShowModal(false);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <AppShell title="COSHH register" back="/registers">
      <div className="card" style={{ marginBottom: 12 }}>
        <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
          Hazardous products on site. Log every product with a hazard label (cement, foam, paints).
        </p>
      </div>

      {error && <div className="banner banner-error">{error}</div>}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-primary" onClick={startAdd} style={{ marginBottom: 12 }}>
        + Add COSHH item
      </button>
        <button type="button" className="btn btn-ghost" onClick={() => setBulkOpen(true)}>Bulk import (CSV)</button>
      </div>

      {loading ? (
        <LoadingIndicator />
      ) : items.length === 0 ? (
        <div className="card" style={{ textAlign: 'center' }}>
          <p style={{ margin: 0, color: 'var(--ink-soft)' }}>No COSHH items yet.</p>
        </div>
      ) : (
        <ul className="person-list">
          {items.map((c) => (
            <li key={c.id}>
              <button type="button" className="site-item-link" onClick={() => startEdit(c)} style={{ width: '100%', textAlign: 'left', font: 'inherit' }}>
                <div>
                  <p className="site-name">{c.product}</p>
                  <p className="site-meta">
                    {c.used_for ? c.used_for : 'No use noted'}
                    {c.hazards ? ` · ${c.hazards}` : ''}
                  </p>
                </div>
                <span className="chev">›</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <Modal open={showModal} title={editing ? 'Edit COSHH' : 'Add COSHH'} onClose={() => { setShowModal(false); setEditing(null); }}>
        <form onSubmit={onSave}>
          <div className="field"><label htmlFor="coshh-product">What is it? *</label><input id="coshh-product" type="text" required placeholder="e.g. Portland cement, MS Polymer sealant" value={draft.product} onChange={(e) => setDraft({ ...draft, product: e.target.value })} autoFocus /></div>
          <div className="field"><label htmlFor="coshh-used-for">What do you use it for?</label><input id="coshh-used-for" type="text" placeholder="e.g. Mortar mixing" value={draft.used_for} onChange={(e) => setDraft({ ...draft, used_for: e.target.value })} /></div>
          <div className="field">
            <label htmlFor="coshh-hazards">What's the danger?</label>
            <div className="input-with-mic">
              <input id="coshh-hazards" type="text" placeholder="e.g. Burns skin, harmful to breathe in" value={draft.hazards} onChange={(e) => setDraft({ ...draft, hazards: e.target.value })} />
              <DictateButton onTranscript={(t) => setDraft((d) => ({ ...d, hazards: d.hazards ? d.hazards + ' ' + t : t }))} />
            </div>
          </div>
          <div className="field">
            <label htmlFor="coshh-controls">How do we make it safe?</label>
            <div className="input-with-mic">
              <textarea id="coshh-controls" rows={2} placeholder="e.g. Wear nitrile gloves and goggles. Mix outdoors. Wash skin if splashed. First aid kit nearby." value={draft.controls} onChange={(e) => setDraft({ ...draft, controls: e.target.value })} />
              <DictateButton onTranscript={(t) => setDraft((d) => ({ ...d, controls: d.controls ? d.controls + ' ' + t : t }))} />
            </div>
          </div>

          <div className="btn-stack">
            <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Saving…' : 'Save'}</button>
            {editing && <button type="button" className="btn btn-ghost" style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={() => onArchive(editing)}>Archive</button>}
            <button type="button" className="btn btn-ghost" onClick={() => { setShowModal(false); setEditing(null); }}>Cancel</button>
          </div>
        </form>
      </Modal>
    <BulkImportModal
        open={bulkOpen}
        title="Bulk import COSHH chemicals"
        columns={[
          { key: 'product', label: 'What is it?', required: true, hint: 'Product name' },
          { key: 'used_for',label: 'Used for',                    hint: 'Optional.' },
          { key: 'hazards', label: "What's the danger?",          hint: 'Optional.' },
          { key: 'controls',label: 'How do we make it safe?',     hint: 'Optional.' },
        ]}
        exampleRows={[
          'product,used_for,hazards,controls',
          'Portland cement,Mortar mixing,Burns skin,Wear gloves and goggles',
          'White spirit,Brush cleaning,Flammable and harmful,Use outdoors and store sealed',
        ]}
        toInput={(row) => row.product?.trim() ? ({
          orgId: org!.org_id,
          product: row.product.trim(),
          used_for: row.used_for?.trim() || null,
          hazards: row.hazards?.trim() || null,
          controls: row.controls?.trim() || null,
        }) : null}
        createRow={async (input) => { await createCoshh(input); }}
        onClose={() => setBulkOpen(false)}
        onImported={async () => { setBulkOpen(false); await refresh(); }}
      />
    </AppShell>
  );
}
